import React, { useEffect, useRef, useState, useCallback } from 'react';
import Task from './Task';

const Timeline = ({ tasks, onStatusChange, onAddTask, onEditTask, onDeleteTask, currentDate, onLoadMoreTasks, hasMoreTasks, isLoadingMore }) => {
  console.log('Timeline received tasks:', tasks);
  console.log('Timeline currentDate:', currentDate);
  console.log('Tasks type:', typeof tasks, 'Is array:', Array.isArray(tasks));
  console.log('Tasks length:', tasks ? tasks.length : 'N/A');
  
  const timelineRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const selectedDate = currentDate ? currentDate.toISOString().split('T')[0] : today;

  // Throttled scroll handler to improve performance
  const handleScroll = useCallback(() => {
    if (!timelineRef.current || !onLoadMoreTasks || !hasMoreTasks || isLoadingMore) {
      return;
    }

    const container = timelineRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Calculate if we're near the bottom (within 200px)
    const nearBottom = scrollTop + clientHeight >= scrollHeight - 200;
    
    if (nearBottom && !isNearBottom) {
      setIsNearBottom(true);
      console.log('Timeline: Near bottom detected, loading more tasks...');
      onLoadMoreTasks();
    } else if (!nearBottom && isNearBottom) {
      setIsNearBottom(false);
    }
  }, [onLoadMoreTasks, hasMoreTasks, isLoadingMore, isNearBottom]);

  // Throttle the scroll event
  useEffect(() => {
    let timeoutId;
    
    const throttledHandleScroll = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(handleScroll, 100); // Throttle to 100ms
    };

    const timelineContainer = timelineRef.current;
    if (timelineContainer) {
      timelineContainer.addEventListener('scroll', throttledHandleScroll, { passive: true });
      
      return () => {
        timelineContainer.removeEventListener('scroll', throttledHandleScroll);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [handleScroll]);

  const groupTasksByDate = (tasks) => {
    const groups = {};
    
    console.log('Grouping tasks by date. Input tasks:', tasks);
    
    // Ensure we have a valid array
    if (!Array.isArray(tasks)) {
      console.warn('Tasks is not an array:', tasks);
      return groups;
    }
    
    // Group tasks by due_date - be more lenient with date handling
    tasks.forEach((task, index) => {
      // Add null/undefined check for task
      if (!task || typeof task !== 'object') {
        console.warn('Encountered invalid task in tasks array at index:', index, task);
        return;
      }
      
      console.log(`Processing task ${index}:`, task);
      console.log(`Task due_date:`, task.due_date);
      console.log(`Task name:`, task.name);
      console.log(`Task project_name:`, task.project_name);
      
      let dateKey = task.due_date;
      
      // Handle different due_date formats and provide fallback
      if (!dateKey || dateKey === 'None' || dateKey === null || dateKey === undefined || dateKey === '') {
        // Use today's date as fallback for tasks without due_date
        dateKey = today;
        console.log(`Task ${index} (${task.name}) has no valid due_date, using today (${today}) as fallback`);
      } else if (typeof dateKey === 'string') {
        // Validate and normalize date string
        try {
          // First, try to parse the date as-is
          let testDate = new Date(dateKey);
          
          // Check if the original string is in YYYY-MM-DD format already
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
            // Already in correct format, just validate it's a valid date
            if (!isNaN(testDate.getTime())) {
              // Keep as-is - dateKey is already correct
              console.log(`Task ${index} (${task.name}) has valid YYYY-MM-DD date: ${dateKey}`);
            } else {
              dateKey = today;
              console.log(`Task ${index} (${task.name}) has invalid YYYY-MM-DD date format, using today as fallback`);
            }
          } else {
            // Try to parse other date formats
            if (!isNaN(testDate.getTime())) {
              // Ensure date is in YYYY-MM-DD format
              dateKey = testDate.toISOString().split('T')[0];
              console.log(`Task ${index} (${task.name}) converted date to: ${dateKey}`);
            } else {
              dateKey = today;
              console.log(`Task ${index} (${task.name}) has invalid due_date format, using today as fallback`);
            }
          }
        } catch (error) {
          dateKey = today;
          console.log(`Task ${index} (${task.name}) due_date parsing error, using today as fallback:`, error);
        }
      } else if (dateKey instanceof Date) {
        // Handle Date objects
        if (!isNaN(dateKey.getTime())) {
          dateKey = dateKey.toISOString().split('T')[0];
          console.log(`Task ${index} (${task.name}) converted Date object to: ${dateKey}`);
        } else {
          dateKey = today;
          console.log(`Task ${index} (${task.name}) has invalid Date object, using today as fallback`);
        }
      } else {
        // Unknown format
        dateKey = today;
        console.log(`Task ${index} (${task.name}) has unknown due_date format (${typeof dateKey}), using today as fallback`);
      }
      
      // Now add the task to the appropriate date group
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(task);
      console.log(`Added task to group ${dateKey}:`, task.name || task.title || 'Untitled');
    });

    console.log('Final grouped tasks:', groups);
    console.log('Group keys:', Object.keys(groups));

    // Sort tasks within each group by a relevant property if needed (e.g., time)
    for (const dateKey in groups) {
      groups[dateKey].sort((a, b) => {
        // Add null checks for sorting
        if (!a || !b) return 0;
        
        // Assuming tasks might have a time property, adjust sorting if needed
        if (a.time && b.time) {
          try {
            const timeA = new Date(`1970-01-01T${a.time}`);
            const timeB = new Date(`1970-01-01T${b.time}`);
            return timeA - timeB;
          } catch (error) {
            return 0;
          }
        } else if (a.time) {
            return -1; // Tasks with time before tasks without
        } else if (b.time) {
            return 1; // Tasks with time before tasks without
        } else {
            return 0; // Maintain original order if no time
        }
      });
    }

    return groups;
  };

  const tasksArray = Array.isArray(tasks) ? tasks : [];
  const groupedTasks = groupTasksByDate(tasksArray);
  
  // Sort dates in ascending order
  const sortedDates = Object.keys(groupedTasks).sort((a, b) => new Date(a) - new Date(b));

  // Scroll to selected date when currentDate changes
  // useEffect(() => {
  //   if (selectedDate && timelineRef.current) {
  //     const targetElement = document.getElementById(`date-${selectedDate}`);
  //     if (targetElement) {
  //       console.log('Scrolling to date:', selectedDate);
  //       targetElement.scrollIntoView({ 
  //         behavior: 'smooth', 
  //         block: 'center' 
  //       });
  //     } else {
  //       console.log('Date element not found for:', selectedDate);
  //     }
  //   }
  // }, [selectedDate]);

  useEffect(() => {
    if (selectedDate && timelineRef.current) {
      const targetElement = document.getElementById(`date-${selectedDate}`);
      
      if (targetElement) {
        // Get the timeline container's scroll position and height
        const container = timelineRef.current;
        const containerScrollTop = container.scrollTop;
        const containerClientHeight = container.clientHeight;
        
        // Get the target element's position and height relative to the viewport
        const targetRect = targetElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate target element's position relative to the scroll container
        const targetRelativeTop = targetRect.top - containerRect.top + containerScrollTop;
        const targetRelativeBottom = targetRect.bottom - containerRect.top + containerScrollTop;

        // Check if the target element is already fully or partially in view
        const isTargetInView = 
          targetRelativeTop >= containerScrollTop && 
          targetRelativeBottom <= (containerScrollTop + containerClientHeight);

        if (!isTargetInView) {
          console.log('Scrolling to date:', selectedDate);
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        } else {
          console.log('Date element is already in view, no scroll needed for:', selectedDate);
        }
      } else {
        console.log('Date element not found for:', selectedDate);
      }
    }
  }, [selectedDate]); // Dependency array remains the same

  return (
    <div className="timeline-container" ref={timelineRef}>
      <div className="timeline">
        {sortedDates.length > 0 ? (
          <>
            {sortedDates.map((date) => (
              <div 
                key={date} 
                id={`date-${date}`}
                className={`timeline-item ${date === today ? 'today' : 'normalday'} ${date === selectedDate ? 'selected-date' : 'selected-date'}`}
              >
                <div className="timeline-date">
                  {new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  {/* {date === selectedDate && (
                    <div style={{ fontSize: '10px', marginTop: '2px', color: '#007bff', fontWeight: 'bold' }}>
                      ← Selected
                    </div>
                  )} */}
                </div>
                <div className="timeline-content">
                  <div className="task-list">
                    {groupedTasks[date].map((task) => (
                      <Task
                        key={task.id}
                        task={task}
                        onStatusChange={onStatusChange}
                        onEditTask={onEditTask}
                        onDeleteTask={onDeleteTask}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading indicator for infinite scroll */}
            {isLoadingMore && (
              <div className="timeline-loading">
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Loading more tasks...</span>
                </div>
              </div>
            )}
            
            {/* End of list indicator */}
            {!hasMoreTasks && tasksArray.length > 0 && (
              <div className="timeline-end">
                <div className="end-indicator">
                  <i className="fas fa-check-circle"></i>
                  <span>You've reached the end of your tasks</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <h3>No tasks found</h3>
            <p>Click the + button to add a new task.</p>
            <p><strong>Debug Info:</strong></p>
            <p>Tasks received: {tasks ? tasks.length : 0}</p>
            <p>Tasks type: {typeof tasks}</p>
            <p>Is tasks array: {Array.isArray(tasks) ? 'Yes' : 'No'}</p>
            <p>Current date: {selectedDate}</p>
            <p>Available dates: {sortedDates.join(', ') || 'None'}</p>
            <p>Sample task data: {tasks && tasks.length > 0 ? JSON.stringify(tasks[0], null, 2) : 'No tasks'}</p>
          </div>
        )}
      </div>
      
      {onAddTask && (
        <button
          className="add-task-btn"
          onClick={onAddTask}
          aria-label="Add new task"
        >
          <i className="fas fa-plus"></i>
        </button>
      )}
    </div>
  );
};

export default Timeline; 